import { useEffect, useState } from "react";
import { getToday } from "@/lib/dateManager";

/**
 * Returns today's `YYYY-MM-DD` string and keeps it fresh across a long-lived
 * session — a tab left open across midnight (or a week/month/quarter
 * rollover) will eventually see the new date without a manual refresh.
 *
 * This exists because `useMemo(getToday, [])` (the naive approach) freezes
 * "today" at mount time forever. That frozen value silently feeds the goal
 * period anchor, the confetti trigger, and the calendar navigation — all of
 * which then keep acting on a stale period once the real day has rolled
 * over. This hook is the fix: it re-derives `getToday()` on the same 60s
 * interval idiom as `useTodayReminderPassed`, but only commits a state
 * update (and thus only re-renders consumers) when the computed string
 * actually differs from the last known value, so it stays a no-op on every
 * tick that doesn't cross a day boundary.
 */
export function useLiveToday(): string {
  const [todayStr, setTodayStr] = useState(() => getToday());

  useEffect(() => {
    const id = setInterval(() => {
      const next = getToday();
      setTodayStr((prev) => (prev === next ? prev : next));
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  return todayStr;
}
